import { type Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { Appointment } from '@adornis/buchungsheld-db/entities/appointment.js';
import type { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import '@adornis/buchungsheld-theme/components/tr-chip.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { DateTime } from 'luxon';

const renderTime = stateful<{ time: DateTime; heading: Renderable }>(({ time, heading }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);

  return html`
    <tr-flex
      space="xxs"
      ${css({
        background: theme?.colors.light.shade(300),
        color: 'black',
        borderRadius: theme?.sizes.borderRadius,
        padding: `${theme?.spacing.sm} ${theme?.spacing.md}`,
      })}
    >
      <tr-text small>${heading}</tr-text>
      <tr-h2 bold ${css({ width: 'max-content' })}> ${time.day} ${time.monthShort} </tr-h2>
      <tr-text bold> ${time.toLocaleString(DateTime.TIME_24_SIMPLE)} </tr-text>
    </tr-flex>
  `;
});

export const appointmentCard = stateful<{
  appointment: NarrowedEntity<Appointment, { _id: 1; start: 1; end: 1; location: 1; name: 1; isNonBlocking: 1 }>;
}>(({ appointment }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);
  const translate = clientTranslator.translate.bind(clientTranslator);

  return html`
    <tr-flex space="lg">
      <tr-flex space="sm">
        <tr-flex horizontal crossaxis-center space="sm">
          ${appointment.name ? html` <tr-text bold>Name: ${appointment.name} </tr-text> ` : nothing}
          ${appointment.isNonBlocking
            ? html`
                <tr-chip ${css({ background: theme?.colors.tone.warning, color: theme?.colors.tone.black })}>
                  ${translate('REGISTRATION', 'non_compulsory')}
                </tr-chip>
              `
            : nothing}
        </tr-flex>
        <tr-text>${translate('mail_template', 'location')}: ${appointment.location ?? '---'}</tr-text>
      </tr-flex>
      <tr-flex
        horizontal
        space="md"
        padding="sm"
        space-between
        crossaxis-center
        ${css({
          border: `1px solid ${theme?.colors.dark.shade(200)}`,
          borderRadius: theme?.spacing.md,
        })}
      >
        ${(() => {
          if (!appointment.start) return html`<i> TBD </i>`;

          const h = [] as any[];
          h.push(renderTime({ time: appointment.start, heading: translate('from') }));
          if (appointment.end) {
            h.push(html`<tr-icon huge> arrow_right_alt </tr-icon>`);
            h.push(renderTime({ time: appointment.end, heading: translate('till') }));
          }

          return h;
        })()}
      </tr-flex>
    </tr-flex>
  `;
});

@customElement('bh-prompt-workshop-appointments-view')
export class BHPromptWorkshopAppointmentsView extends TrDialog<void> {
  static override get element_name() {
    return 'bh-prompt-workshop-appointments-view';
  }

  @property({ attribute: false }) workshop = new RXController<Maybe<Workshop>>(this, undefined);
  @property({ attribute: false }) terminColor = '';
  @property({ attribute: false }) terminFontColor = '';

  override content() {
    if (!this.workshop.value) return html` <tr-loading-state></tr-loading-state> `;

    return html`
      <tr-flex space="md">
        <tr-h3 bold>${this._translation.translate('termin', 'all_termins')}</tr-h3>
        ${this.workshop.value.appointments.map((appointment, index) => {
          return html` ${index !== 0 ? html` <x-hr></x-hr> ` : nothing} ${appointmentCard({ appointment })} `;
        })}
      </tr-flex>
    `;
  }
}
